import type { CSSProperties, ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "./button";

/**
 * Rules (board 0c): an empty state always tells the next action, never just
 * "Aucune donnée"; an error says what is lost and what is not.
 */
export function EmptyState({ art, title, children, action, className }: { art?: ReactNode; title: string; children?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-2xl border border-dashed border-line-strong bg-white/60 px-5 py-8 text-center", className)}>
      {art && <div className="mx-auto mb-3.5 flex justify-center">{art}</div>}
      <div className="text-sm font-bold text-ink">{title}</div>
      {children && <div className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted">{children}</div>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function ErrorState({ title = "Échec du chargement", children = "Une erreur est survenue.", onRetry, art, className }: { title?: string; children?: ReactNode; onRetry?: () => void; art?: ReactNode; className?: string }) {
  return (
    <div role="alert" className={cn("rounded-2xl border border-danger-line bg-[#FDF7F6] px-5 py-8 text-center", className)}>
      <div className="mx-auto mb-3.5 flex justify-center">
        {art ?? (
          <span className="inline-flex size-11 items-center justify-center rounded-xl bg-danger-bg">
            <AlertCircle className="size-5 text-danger-500" />
          </span>
        )}
      </div>
      <div className="text-[13px] font-bold text-ink">{title}</div>
      <div className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted">{children}</div>
      {onRetry && (
        <Button variant="outline" size="sm" className="mt-3.5" onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </div>
  );
}

/** Grey shimmer bar. Skeletons mirror the real layout; never a full-screen spinner. */
export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <span
      aria-hidden
      style={style}
      className={cn(
        "block rounded-md bg-line bg-[linear-gradient(90deg,transparent,rgb(255_255_255/0.6),transparent)] bg-[length:200%_100%] animate-[shine_1.4s_linear_infinite]",
        className,
      )}
    />
  );
}

export function SkeletonRows({ rows = 3, label = "Chargement…" }: { rows?: number; label?: string }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col gap-3.5">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="size-8 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-2.5 w-2/5" />
            <Skeleton className="h-2 w-1/4" />
          </div>
          <Skeleton className="h-2.5 w-16" />
        </div>
      ))}
    </div>
  );
}
